"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import ApexCharts from "apexcharts";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  IconButton,
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import GetAppIcon from "@mui/icons-material/GetApp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "../common/components/LocalizationProvider";
import { utils, writeFile } from "xlsx";
// Lazy load do DeviceGridDialog para melhor performance
const DeviceGridDialog = lazy(() => import("../common/components/DeviceGridDialog"));

// ---- globals for apex safety ----
declare global {
  interface Window {
    ApexCharts?: { colors?: string[] };
    Apex?: { colors?: string[] };
  }
}

// ---- sizes (top-level so styled() can access) ----
const PIE_HEIGHT = 300;
const PIE_WRAPPER_HEIGHT = 300;
const BAR_HEIGHT = 520;
const BAR_WRAPPER_HEIGHT = 520;
const ACTIVITY_BAR_HEIGHT = 580;
const ACTIVITY_BAR_WRAPPER_HEIGHT = 580;

// ---- types ----
interface StateCountsType {
  [key: string]: any[];
}

// ---- color mode context (keeps your toggler) ----
const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

// ---- styled components ----
const Root = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
}));


const Content = styled(Container)(({ theme }) => ({
  maxWidth: "100%",
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(4),
  paddingLeft: 0,
  paddingRight: 0,
  flex: 1,
  overflow: "auto",
  backgroundColor: theme.palette.mode === "dark" ? "#0d1117" : "#f6f8fa",
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.25),
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.mode === "dark" ? "#161b22" : "#ffffff",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.08)"}`,
  boxShadow: "none",
  minWidth: 0,
  flex: "1 1 auto",
  height: "100%",
}));

const PieWrapper = styled("div")({
  height: PIE_WRAPPER_HEIGHT,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const BarWrapper = styled("div")({
  height: BAR_WRAPPER_HEIGHT,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const ActivityBarWrapper = styled("div")(({ theme }) => ({
  height: ACTIVITY_BAR_WRAPPER_HEIGHT,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  overflowY: "auto",
  overflowX: "hidden",
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: 3,
  },
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 800,
  marginBottom: theme.spacing(1),
  textAlign: "left",
  textTransform: "uppercase",
  color: theme.palette.text.primary,
}));

const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
}));

// ---- KPI Alert Card (Compact) ----
const KpiAlertCard = styled(Paper)<{ severity: 'error' | 'warning' | 'info' | 'success' }>(({ theme, severity }) => {
  const colors = {
    error: { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#ef4444' },
    warning: { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', text: '#f59e0b' },
    info: { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', text: '#3b82f6' },
    success: { bg: 'rgba(34, 197, 94, 0.1)', border: '#22c55e', text: '#22c55e' },
  };
  const c = colors[severity];
  return {
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    backgroundColor: theme.palette.mode === 'dark' ? c.bg : c.bg,
    border: `1px solid ${c.border}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 160,
    flex: '1 1 0',
    '& .kpi-value': {
      fontSize: 22,
      fontWeight: 700,
      color: c.text,
      lineHeight: 1,
    },
    '& .kpi-label': {
      fontSize: 10,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
    },
    '& .kpi-trend': {
      fontSize: 9,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    },
  };
});

// ---- Fleet Activity Card (exact same style as ChartContainer) ----
const FleetActivityCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.25),
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? '#161b22' : '#ffffff',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.08)'}`,
  boxShadow: 'none',
  minWidth: 0,
  flex: '1 1 auto',
  height: '100%',
}));

// ---- Vehicle Condition Card ----
const ConditionCard = styled(Box)<{ condition: 'good' | 'satisfactory' | 'critical' }>(({ theme, condition }) => {
  const isDark = theme.palette.mode === 'dark';
  const colors = {
    good: { bg: isDark ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)', accent: '#22c55e' },
    satisfactory: { bg: isDark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.1)', accent: '#f59e0b' },
    critical: { bg: isDark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.1)', accent: '#ef4444' },
  };
  const c = colors[condition];
  return {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: c.bg,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    '& .condition-dot': {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: c.accent,
    },
    '& .condition-value': {
      fontSize: 18,
      fontWeight: 700,
      color: c.accent,
    },
    '& .condition-label': {
      fontSize: 11,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
    },
  };
});

// ---- Trips Progress Bar ----
const TripsProgressContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  '& .progress-bar': {
    height: 28,
    borderRadius: 14,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
    overflow: 'hidden',
    display: 'flex',
  },
  '& .progress-segment': {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: '#fff',
    transition: 'width 0.5s ease',
    minWidth: 0,
    overflow: 'hidden',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  '& .progress-legend': {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  '& .legend-item': {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  '& .legend-dot': {
    width: 10,
    height: 10,
    borderRadius: 3,
    flexShrink: 0,
  },
}));

// ---- main component ----
const Overview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useTranslation();
  const isDark = theme.palette.mode === "dark";

  // data states
  const [devices, setDevices] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Menu states
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedChart, setSelectedChart] = useState<string | null>(null);

  // Grid dialog states
  const [gridOpen, setGridOpen] = useState(false);
  const [gridFilter, setGridFilter] = useState<{
    deviceIds: number[];
    deviceNames: string[];
    filterLabel: string;
    colorIndex: number;
  }>({ deviceIds: [], deviceNames: [], filterLabel: '', colorIndex: 0 });

  // refs for charts
  const pie1Ref = useRef<HTMLDivElement | null>(null);
  const pie2Ref = useRef<HTMLDivElement | null>(null);
  const pie3Ref = useRef<HTMLDivElement | null>(null);
  const bar1Ref = useRef<HTMLDivElement | null>(null);
  const bar2Ref = useRef<HTMLDivElement | null>(null);
  const bar3Ref = useRef<HTMLDivElement | null>(null);

  const lastUpdateRef = useRef(0);

  // store group data for CSV export
  const pieGroupsRef = useRef<{ [k: string]: Record<string, any[]> }>({
    pie1: {},
    pie2: {},
    pie3: {},
  });

  const barGroupsRef = useRef<{ [k: string]: any[] }>({
    bar1: [],
    bar2: [],
    bar3: [],
  });

  // Store chart data for export
  const chartDataRef = useRef<Record<string, any>>({});

  const textColor = theme.palette.text.primary;

  // Cores específicas para os gráficos (independente do tema do app)
  const chartPrimary = isDark ? "#90caf9" : "#1976D2";  // Azul
  const chartSecondary = isDark ? "#81c784" : "#4CAF50"; // Verde

  const colorsArrRef = useRef<string[]>([]);

  // Device Map para O(1) lookup - OTIMIZAÇÃO CRÍTICA para 3000+ devices
  const deviceMap = useMemo(() => {
    const map = new Map<number, any>();
    devices.forEach(d => map.set(d.id, d));
    return map;
  }, [devices]);

  // Position Map para O(1) lookup por deviceId
  const positionMap = useMemo(() => {
    const map = new Map<number, any>();
    positions.forEach(p => map.set(p.deviceId, p));
    return map;
  }, [positions]);

  // ---- KPI Metrics com variação ----
  const kpiMetrics = useMemo(() => {
    const now = Date.now();
    let errorsCount = 0;
    let warningsCount = 0;
    let onlineCount = 0;
    let movingCount = 0;
    let stoppedCount = 0;
    let idleCount = 0;
    let lowBattery = 0;
    let offlineCount = 0;

    devices.forEach((d: any) => {
      const pos = positionMap.get(d.id);
      const lastUpdate = new Date(d.lastUpdate).getTime();
      const isOffline = now - lastUpdate > 48 * 3600 * 1000;
      const isOnline = d.status === 'online';

      if (isOnline) onlineCount++;
      if (isOffline) offlineCount++;

      // Check for alarms
      if (pos?.attributes?.alarm) {
        if (['sos', 'vibration', 'powerCut', 'tampering'].includes(pos.attributes.alarm)) {
          errorsCount++;
        } else {
          warningsCount++;
        }
      }

      // Low battery
      if (pos?.attributes?.batteryLevel && pos.attributes.batteryLevel < 20) {
        lowBattery++;
        warningsCount++;
      }

      // Movement status
      if (pos) {
        if (pos.attributes?.motion) movingCount++;
        else if (pos.attributes?.ignition && !pos.speed) idleCount++;
        else stoppedCount++;
      }
    });

    const totalDevices = devices.length;
    const activePercent = totalDevices > 0 ? Math.round((onlineCount / totalDevices) * 100) : 0;

    // Mock variations (in real app, compare with previous period)
    const variations = {
      errors: Math.floor(Math.random() * 6) - 3, // -3 to +2
      warnings: Math.floor(Math.random() * 8) - 4, // -4 to +3
      offline: Math.floor(Math.random() * 5) - 2, // -2 to +2
      active: Math.floor(Math.random() * 6) - 2, // -2 to +3
    };

    return {
      errors: errorsCount,
      warnings: warningsCount,
      offline: offlineCount,
      lowBattery,
      online: onlineCount,
      moving: movingCount,
      stopped: stoppedCount,
      idle: idleCount,
      total: totalDevices,
      activePercent,
      variations,
    };
  }, [devices, positionMap]);

  // ---- Vehicle Condition distribution ----
  const vehicleCondition = useMemo(() => {
    let good = 0;
    let satisfactory = 0;
    let critical = 0;

    devices.forEach((d: any) => {
      const pos = positionMap.get(d.id);
      const lastUpdate = new Date(d.lastUpdate).getTime();
      const now = Date.now();
      const hoursSinceUpdate = (now - lastUpdate) / (1000 * 3600);

      // Scoring logic
      let score = 100;

      // Penalize for time since last update
      if (hoursSinceUpdate > 48) score -= 50;
      else if (hoursSinceUpdate > 24) score -= 25;
      else if (hoursSinceUpdate > 12) score -= 10;

      // Penalize for alarms
      if (pos?.attributes?.alarm) {
        const criticalAlarms = ['sos', 'powerCut', 'tampering'];
        if (criticalAlarms.includes(pos.attributes.alarm)) score -= 40;
        else score -= 15;
      }

      // Penalize for low battery
      if (pos?.attributes?.batteryLevel) {
        if (pos.attributes.batteryLevel < 10) score -= 30;
        else if (pos.attributes.batteryLevel < 20) score -= 15;
      }

      if (score >= 70) good++;
      else if (score >= 40) satisfactory++;
      else critical++;
    });

    return { good, satisfactory, critical };
  }, [devices, positionMap]);

  // ---- Trips Performance (mock data - would come from trips API) ----
  const tripsPerformance = useMemo(() => {
    // In real app, fetch from /api/reports/trips
    const total = Math.max(1, Math.floor(devices.length * 0.8));
    const onTime = Math.floor(total * 0.72);
    const delayed = Math.floor(total * 0.18);
    const canceled = total - onTime - delayed;

    return {
      total,
      onTime,
      delayed,
      canceled,
      onTimePercent: Math.round((onTime / total) * 100),
      delayedPercent: Math.round((delayed / total) * 100),
      canceledPercent: Math.round((canceled / total) * 100),
    };
  }, [devices.length]);

  // Função para formatar números com separadores de milhar
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  // ---- APPLY EXACT COLORS ARRAY (cores específicas para gráficos) ----
  useEffect(() => {
    const colorsArr = [
      chartSecondary, // Verde (movimento, etc)
      "#FF4560", // Red
      "#F86624", // Orange
      "#775DD0", // Purple
      "#3F51B5", // Indigo
      "#546E7A", // Blue-gray
      "#D4526E", // Pink
      "#8D5B4C", // Brown
      "#A5978B", // Taupe
      "#4ECDC4", // Teal
      chartPrimary, // Azul
      "#C7F464", // Lime
      "#81D4FA", // Light Blue
      "#FD6A02", // Tangerine
      "#7B1FA2", // Dark Purple
      "#FFC300", // Mustard
      "#FF5733", // Burnt Orange
      "#C70039", // Crimson
      "#900C3F"]; // Dark Red

    colorsArrRef.current = colorsArr; // Armazena na ref

    (window as any).Apex = (window as any).Apex || {};
    (window as any).Apex.colors = colorsArr;

    if (window.ApexCharts) {
      window.ApexCharts.colors = colorsArr;
    }

    try {
      (ApexCharts as any).defaultOptions = (ApexCharts as any).defaultOptions || {};
      (ApexCharts as any).defaultOptions.colors = colorsArr;
    } catch (e) {
      // ignore
    }
  }, [chartPrimary, chartSecondary]);

// ---- fetch devices & positions (keep your endpoints) ----
useEffect(() => {
  const fetchData = async () => {
    try {
      const devResp = await fetch("/api/devices");
      if (!devResp.ok) {
        const errorText = await devResp.text();
        throw new Error(`Erro ao buscar devices: ${devResp.status} - ${errorText}`);
      }

      // Check if response is JSON
      const contentType = devResp.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await devResp.text();
        throw new Error(`Resposta da API não é JSON: ${textResponse.substring(0, 100)}...`);
      }

      const devs = await devResp.json();

      const posResp = await fetch("/api/positions");
      if (!posResp.ok) {
        const errorText = await posResp.text();
        throw new Error(`Erro ao buscar positions: ${posResp.status} - ${errorText}`);
      }

      // Check if response is JSON
      const posContentType = posResp.headers.get("content-type");
      if (!posContentType || !posContentType.includes("application/json")) {
        const textResponse = await posResp.text();
        throw new Error(`Resposta da API não é JSON: ${textResponse.substring(0, 100)}...`);
      }

      const poss = await posResp.json();

      setDevices(devs || []);
      setPositions(poss || []);
      setLoading(false);
    } catch (e) {
      console.error("Erro ao carregar dados:", e);
      setError("Erro ao carregar dados: " + (e instanceof Error ? e.message : String(e)));
      setLoading(false);
    }
  };
  fetchData();
}, []);

  // ---- base options helper ----
  const baseOptions = (overrides = {}) => ({
    chart: { background: "transparent", foreColor: textColor },
    theme: { mode: isDark ? "dark" : "light" },
    tooltip: { theme: isDark ? "dark" : "light" },
    legend: {
      labels: { colors: textColor },
      fontSize: "12px",
    },
    ...overrides,
  });

  // ---- utility: CSV helpers ----
  const escapeCsv = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(",") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const downloadCSV = (filename: string, csvContent: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", filename);
    a.click();
    URL.revokeObjectURL(url);
  };

  const groupsObjToArray = (groupsObj: Record<string, any[]>) =>
    Object.keys(groupsObj).map((label) => ({ label, items: groupsObj[label] || [] }));

  const exportGroupsAsCSV = (groupsObj: Record<string, any[]>, filename = "groups.csv") => {
    const groups = groupsObjToArray(groupsObj);
    const rows: string[] = [];
    rows.push(["Group,Device"].map(escapeCsv).join(","));
    groups.forEach((g) => {
      const items = (g.items || []).map((id) => {
        const dev = devices.find((d: any) => d.id === id || d.id === (id as any));
        if (dev) return dev.name || dev.id || id;
        return typeof id === "string" ? id : (id && id.name) || id;
      });
      items.forEach((it) => rows.push([escapeCsv(g.label), escapeCsv(it)].join(",")));
    });
    downloadCSV(filename, rows.join("\n"));
  };

  // Export chart data functions
  const exportChartCSV = (chartId: string) => {
    const data = chartDataRef.current[chartId];
    if (!data) return;

    let rows: string[] = [];
    if (data.labels && data.series) {
      // Pie chart data
      rows.push("Label,Value");
      data.labels.forEach((label: string, i: number) => {
        rows.push(`${escapeCsv(label)},${data.series[i]}`);
      });
    } else if (data.categories && data.series) {
      // Bar chart data
      rows.push("Category,Value");
      data.categories.forEach((category: string, i: number) => {
        rows.push(`${escapeCsv(category)},${data.series[0].data[i]}`);
      });
    }

    downloadCSV(`${chartId}.csv`, rows.join("\n"));
    setMenuAnchor(null);
  };

  const exportChartXLSX = (chartId: string) => {
    const data = chartDataRef.current[chartId];
    if (!data) return;

    let rows: any[] = [];
    if (data.labels && data.series) {
      // Pie chart data
      rows = data.labels.map((label: string, i: number) => ({
        Label: label,
        Value: data.series[i]
      }));
    } else if (data.categories && data.series) {
      // Bar chart data
      rows = data.categories.map((category: string, i: number) => ({
        Category: category,
        Value: data.series[0].data[i]
      }));
    }

    const ws = utils.json_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, chartId);
    writeFile(wb, `${chartId}.xlsx`);
    setMenuAnchor(null);
  };

  // Handle menu opening
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chartId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedChart(chartId);
  };

  // Handle menu closing
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedChart(null);
  };

  // Handle grid open from chart click - usando deviceMap O(1)
  const handleOpenGrid = useCallback((deviceIds: number[], filterLabel: string, colorIndex: number) => {
    const deviceNames = deviceIds.map(id => {
      const dev = deviceMap.get(id);
      return dev?.name || String(id);
    });
    setGridFilter({ deviceIds, deviceNames, filterLabel, colorIndex });
    setGridOpen(true);
  }, [deviceMap]);

  const handleCloseGrid = useCallback(() => {
    setGridOpen(false);
  }, []);

  // ---- TOP PIES (3) ----
  useEffect(() => {
    if (!pie1Ref.current || !pie2Ref.current || !pie3Ref.current || loading) return;

    // PIE 1 - Last update grouping
    const stateCounts: StateCountsType = { Online: [], "12h": [], "24h": [], "36h": [], "48h": [], "+48h": [] };
    devices.forEach((d: any) => {
      const last = new Date().getTime() - new Date(d.lastUpdate).getTime();
      if (d.status === "online") stateCounts.Online.push(d.id ?? d.name ?? d);
      else if (last < 12 * 3600 * 1000) stateCounts["12h"].push(d.id ?? d.name ?? d);
      else if (last < 24 * 3600 * 1000) stateCounts["24h"].push(d.id ?? d.name ?? d);
      else if (last < 36 * 3600 * 1000) stateCounts["36h"].push(d.id ?? d.name ?? d);
      else if (last < 48 * 3600 * 1000) stateCounts["48h"].push(d.id ?? d.name ?? d);
      else stateCounts["+48h"].push(d.id ?? d.name ?? d);
    });

    // save for export
    pieGroupsRef.current.pie1 = stateCounts;

    const p1Keys = Object.keys(stateCounts);
    const p1Series = p1Keys.map((k) => (stateCounts[k] || []).length);

    // Store chart data for export
    chartDataRef.current.pie1 = {
      labels: p1Keys,
      series: p1Series
    };

    const pie1 = new ApexCharts(
      pie1Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "donut",
            height: PIE_HEIGHT,
            width: "100%",
            events: {
              dataPointSelection: (_event: any, _chartContext: any, config: any) => {
                const { dataPointIndex } = config;
                const selectedLabel = p1Keys[dataPointIndex];
                const selectedDeviceIds = stateCounts[selectedLabel] || [];
                handleOpenGrid(selectedDeviceIds, selectedLabel, dataPointIndex);
              }
            }
          },
          series: p1Series,
          labels: p1Keys,
          plotOptions: {
            pie: {
              donut: {
                size: "70%",
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontSize: '14px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                    color: textColor,
                    offsetY: -10,
                  },
                  value: {
                    show: true,
                    fontSize: '20px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                    color: textColor,
                    offsetY: 10,
                    formatter: (val: any) => val
                  },
                  total: {
                    show: true,
                    label: 'Total',
                    fontSize: '12px',
                    color: textColor,
                    formatter: (w: any) => w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)
                  }
                }
              }
            }
          },
          dataLabels: { enabled: true, formatter: (v: any) => Math.round(v) + "%" },
          legend: { position: "right", fontSize: "11px", width: 120, itemMargin: { horizontal: 0, vertical: 2 } },
          tooltip: {
            custom: ({ seriesIndex }: any) => {
              const key = p1Keys[seriesIndex];
              const ids = stateCounts[key] || [];
              // Limitar tooltip a 50 items para performance
              const limitedIds = ids.slice(0, 50);
              const names = limitedIds
                .map((id) => {
                  const dev = deviceMap.get(id);
                  return dev?.name || id;
                })
                .filter(Boolean);
              const moreCount = ids.length - limitedIds.length;
              if (names.length === 0) return `<div style="padding:8px">Nenhum device</div>`;
                return `<div style="padding:8px; max-height:260px; overflow:auto; font-size:12px; line-height:1.3;">
                          <b>${key} (${ids.length})</b>
                          <ul style="margin:6px 0 0 12px; padding:0; list-style: disc;">
                            ${names.map((n) => `<li>${n}</li>`).join("")}
                            ${moreCount > 0 ? `<li><i>... e mais ${moreCount}</i></li>` : ''}
                          </ul>
                        </div>`;
            },
          },
        }),
      }
    );
    pie1.render();

    // PIE 2 - Movement
    const mCounts: StateCountsType = { movendo: [], parado: [], ralenti: [] };
    positions.forEach((p: any) => {
      const devId = p.deviceId ?? (p.device && (p.device.id ?? p.device));
      if (p.attributes && p.attributes.motion) mCounts.movendo.push(devId);
      else if (p.attributes && p.attributes.ignition && !p.speed) mCounts.ralenti.push(devId);
      else mCounts.parado.push(devId);
    });

    pieGroupsRef.current.pie2 = mCounts;

    const p2Keys = ["movendo", "parado", "ralenti"];
    const p2Series = p2Keys.map((k) => (mCounts[k] || []).length);

    // Store chart data for export
    chartDataRef.current.pie2 = {
      labels: ["Em movimento", "Desligado", "Ocioso"],
      series: p2Series
    };

    const p2Labels = ["Em movimento", "Desligado", "Ocioso"];
    const pie2 = new ApexCharts(
      pie2Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "donut",
            height: PIE_HEIGHT,
            width: "100%",
            events: {
              dataPointSelection: (_event: any, _chartContext: any, config: any) => {
                const { dataPointIndex } = config;
                const selectedLabel = p2Labels[dataPointIndex];
                const selectedKey = p2Keys[dataPointIndex];
                const selectedDeviceIds = mCounts[selectedKey] || [];
                handleOpenGrid(selectedDeviceIds, selectedLabel, dataPointIndex);
              }
            }
          },
          series: p2Series,
          labels: p2Labels,
          plotOptions: {
            pie: {
              donut: {
                size: "70%",
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontSize: '14px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                    color: textColor,
                    offsetY: -10,
                  },
                  value: {
                    show: true,
                    fontSize: '20px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                    color: textColor,
                    offsetY: 10,
                    formatter: (val: any) => val
                  },
                  total: {
                    show: true,
                    label: 'Total',
                    fontSize: '12px',
                    color: textColor,
                    formatter: (w: any) => w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)
                  }
                }
              }
            }
          },
          dataLabels: { enabled: true, formatter: (v: any) => Math.round(v) + "%" },
          legend: { position: "right", fontSize: "11px", width: 120, itemMargin: { horizontal: 0, vertical: 2 } },
          tooltip: {
            custom: ({ seriesIndex }: any) => {
              const key = p2Keys[seriesIndex];
              const ids = mCounts[key] || [];
              const limitedIds = ids.slice(0, 50);
              const names = limitedIds
                .map((id) => {
                  const dev = deviceMap.get(id);
                  return dev?.name || id;
                })
                .filter(Boolean);
              const moreCount = ids.length - limitedIds.length;
              if (names.length === 0) return `<div style="padding:8px">Nenhum device</div>`;
                return `<div style="padding:8px; max-height:260px; overflow:auto; font-size:12px; line-height:1.3;">
                          <b>${key} (${ids.length})</b>
                          <ul style="margin:6px 0 0 12px; padding:0; list-style: disc;">
                            ${names.map((n) => `<li>${n}</li>`).join("")}
                            ${moreCount > 0 ? `<li><i>... e mais ${moreCount}</i></li>` : ''}
                          </ul>
                        </div>`;
            },
          },
        }),
      }
    );
    pie2.render();

    // PIE 3 - Alarms
    // Helper para traduzir chaves de alarme
    const translateAlarm = (key: string): string => {
      if (key === "Normal") return t("sharedNoAlarm") || "Normal";
      // Capitaliza primeira letra para formar a chave de tradução: sos -> alarmSos
      const translationKey = `alarm${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      return t(translationKey) || key;
    };

    const alarmCounts: StateCountsType = {};
    alarmCounts["Normal"] = [];
    positions.forEach((p: any) => {
      if (p.attributes && p.attributes.alarm) {
        const a = p.attributes.alarm;
        alarmCounts[a] = alarmCounts[a] || [];
        alarmCounts[a].push(p.deviceId);
      } else {
        alarmCounts["Normal"].push(p.deviceId);
      }
    });

    pieGroupsRef.current.pie3 = alarmCounts;

    const p3Keys = Object.keys(alarmCounts);
    const p3Labels = p3Keys.map(translateAlarm); // Labels traduzidos para exibição
    const p3Series = p3Keys.map((k) => (alarmCounts[k] || []).length);

    // Store chart data for export
    chartDataRef.current.pie3 = {
      labels: p3Labels,
      series: p3Series
    };

    const pie3 = new ApexCharts(
      pie3Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "donut",
            height: PIE_HEIGHT,
            width: "100%",
            events: {
              dataPointSelection: (_event: any, _chartContext: any, config: any) => {
                const { dataPointIndex } = config;
                const selectedKey = p3Keys[dataPointIndex];
                const selectedLabel = p3Labels[dataPointIndex];
                const selectedDeviceIds = alarmCounts[selectedKey] || [];
                handleOpenGrid(selectedDeviceIds, selectedLabel, dataPointIndex);
              }
            }
          },
          series: p3Series,
          labels: p3Labels,
          plotOptions: {
            pie: {
              donut: {
                size: "70%",
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontSize: '14px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                    color: textColor,
                    offsetY: -10,
                  },
                  value: {
                    show: true,
                    fontSize: '20px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                    color: textColor,
                    offsetY: 10,
                    formatter: (val: any) => val
                  },
                  total: {
                    show: true,
                    label: 'Total',
                    fontSize: '12px',
                    color: textColor,
                    formatter: (w: any) => w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)
                  }
                }
              }
            }
          },
          dataLabels: { enabled: true, formatter: (v: any) => Math.round(v) + "%" },
          legend: { position: "right", fontSize: "11px", width: 120, itemMargin: { horizontal: 0, vertical: 2 } },
          tooltip: {
            custom: ({ seriesIndex }: any) => {
              const key = p3Keys[seriesIndex];
              const label = p3Labels[seriesIndex];
              const ids = alarmCounts[key] || [];
              const limitedIds = ids.slice(0, 50);
              const names = limitedIds
                .map((id) => {
                  const dev = deviceMap.get(id);
                  return dev?.name || id;
                })
                .filter(Boolean);
              const moreCount = ids.length - limitedIds.length;
              if (names.length === 0) return `<div style="padding:8px">Nenhum device</div>`;
                return `<div style="padding:8px; max-height:260px; overflow:auto; font-size:12px; line-height:1.3;">
                          <b>${label} (${ids.length})</b>
                          <ul style="margin:6px 0 0 12px; padding:0; list-style: disc;">
                            ${names.map((n) => `<li>${n}</li>`).join("")}
                            ${moreCount > 0 ? `<li><i>... e mais ${moreCount}</i></li>` : ''}
                          </ul>
                        </div>`;
            },
          },
        }),
      }
    );
    pie3.render();

    return () => {
      try {
        pie1.destroy();
        pie2.destroy();
        pie3.destroy();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, positions, loading, isMobile, theme, deviceMap]);

  // ---- BOTTOM BARS (3) ----
  useEffect(() => {
    const currentTime = Date.now();
    if (currentTime - lastUpdateRef.current < 1000) {
      return;
    }
    lastUpdateRef.current = currentTime;

    if (!bar1Ref.current || !bar2Ref.current || !bar3Ref.current || loading) return;

    // BAR 1 - Odometer (usando deviceMap O(1))
    const getOdom = (p: any, d: any) =>
      Math.round(
        ((d && d.attributes && !d.attributes["report.ignoreOdometer"] && p.attributes && p.attributes.odometer) ||
          (p.attributes && p.attributes.totalDistance) ||
          0) / 1000
      );

    const odData = positions
      .filter((p) => getOdom(p, deviceMap.get(p.deviceId)) > 0)
      .sort((a, b) => getOdom(b, deviceMap.get(b.deviceId)) - getOdom(a, deviceMap.get(a.deviceId)))
      .slice(0, 15);

    const odSeries = [{ name: "Km", data: odData.map((p) => getOdom(p, deviceMap.get(p.deviceId))) }];
    const odCats = odData.map((p) => deviceMap.get(p.deviceId)?.name || "");

    barGroupsRef.current.bar1 = odData.map((p) => deviceMap.get(p.deviceId)?.name || p.deviceId);

    chartDataRef.current.bar1 = {
      categories: odCats,
      series: odSeries
    };

    const bar1 = new ApexCharts(
      bar1Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "bar",
            height: BAR_HEIGHT,
            width: "100%",
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "85%",
              borderRadius: 5,
              distributed: true
            }
          },
          series: odSeries,
          xaxis: {
            categories: odCats,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            show: true,
            labels: {
              style: { colors: textColor, fontSize: '11px' },
              maxWidth: 100
            }
          },
          grid: { show: false },
          dataLabels: {
            enabled: true,
            formatter: function(val: any) {
              return formatNumber(val) + ' km';
            },
            style: {
              fontSize: '11px',
              fontWeight: 'bold',
              colors: ["#fff"]
            },
            dropShadow: { enabled: true, top: 1, left: 1, blur: 1, opacity: 0.45 }
          },
          colors: colorsArrRef.current,
          tooltip: { enabled: true }
        }),
      }
    );
    bar1.render();

    // BAR 2 - Hours (Horímetro) - usando deviceMap O(1)
    const getHours = (p: any) => (p.attributes && p.attributes.hours ? Math.round(p.attributes.hours / 1000 / 3600) : 0);
    const hrData = positions.filter((p) => getHours(p) > 0).sort((a, b) => getHours(b) - getHours(a)).slice(0, 15);
    const hrSeries = [{ name: "Horas", data: hrData.map((p) => getHours(p)) }];
    const hrCats = hrData.map((p) => deviceMap.get(p.deviceId)?.name || "");

    barGroupsRef.current.bar2 = hrData.map((p) => deviceMap.get(p.deviceId)?.name || p.deviceId);

    chartDataRef.current.bar2 = {
      categories: hrCats,
      series: hrSeries
    };

    const bar2 = new ApexCharts(
      bar2Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "bar",
            height: BAR_HEIGHT,
            width: "100%",
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "85%",
              borderRadius: 5,
              distributed: true
            }
          },
          series: hrSeries,
          xaxis: {
            categories: hrCats,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            show: true,
            labels: {
              style: { colors: textColor, fontSize: '11px' },
              maxWidth: 100
            }
          },
          grid: { show: false },
          dataLabels: {
            enabled: true,
            formatter: function(val: any) {
              return formatNumber(val) + ' h';
            },
            style: {
              fontSize: '11px',
              fontWeight: 'bold',
              colors: ["#fff"]
            },
            dropShadow: { enabled: true, top: 1, left: 1, blur: 1, opacity: 0.45 }
          },
          colors: colorsArrRef.current,
          tooltip: { enabled: true }
        }),
      }
    );
    bar2.render();

  // BAR 3 - Activity (rangeBar)
  const currentDate = new Date(); // Renamed from 'now' to 'currentDate'
  const from = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
  const to = from + 1000 * 60 * 60 * 24;
  const _devices = [...devices].slice(0, 20); // Reduce from 30 to 10

  // BAR 3 - Activity (rangeBar) - Taller chart for better visibility
  const chart3 = new ApexCharts(
    bar3Ref.current,
    {
      ...baseOptions({
        chart: { type: "rangeBar", height: ACTIVITY_BAR_HEIGHT, width: "100%" },
        plotOptions: {
          bar: {
            horizontal: true,
            rangeBarGroupRows: true,
            //barHeight: "60%",
            borderRadius: 6 // Bordas arredondadas como no dash2 copy
          }
        },
        series: [],
        xaxis: {
          min: from,
          max: to,
          type: "datetime",
          labels: {
            style: {
              colors: textColor
            }
          }
        },
        legend: {
          position: isMobile ? "bottom" : "right",
          fontSize: "11px",
          labels: {
            colors: textColor
          }
        },
        grid: {
          show: true, // Mostrar grid como no dash2 copy
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        tooltip: {
          custom: ({ seriesIndex, dataPointIndex, w }: any) => {
            const data = w.config.series[seriesIndex]?.data?.[dataPointIndex];
            if (!data) return '';
            const seriesName = w.config.series[seriesIndex]?.name || '';
            const deviceName = data.x || '';
            const startTime = new Date(data.y[0]);
            const endTime = new Date(data.y[1]);
            const durationMs = endTime.getTime() - startTime.getTime();
            const hours = Math.floor(durationMs / (1000 * 60 * 60));
            const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
            const formatTime = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const bgColor = seriesName === 'Paradas' ? chartPrimary : chartSecondary;
            return `
              <div style="padding: 8px 12px; background: ${bgColor}; color: white; border-radius: 4px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${deviceName}</div>
                <div style="font-size: 12px;">${seriesName}</div>
                <div style="font-size: 11px; margin-top: 4px;">
                  ${formatTime(startTime)} - ${formatTime(endTime)}
                </div>
                <div style="font-size: 13px; font-weight: bold; margin-top: 4px;">
                  Duração: ${hours}h ${minutes}min
                </div>
              </div>
            `;
          }
        }
      }),
    }
  );
  chart3.render();

  // fetch trips/stops for activity
  const fetchTripsStops = async () => {
    try {
      const deviceIds = _devices.map((d) => `deviceId=${d.id}`).join("&");
      const dateParam = `&from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`;
      const [tripsResp, stopsResp] = await Promise.all([
        fetch(`/api/reports/trips?${deviceIds}${dateParam}`, { headers: { Accept: "application/json" } }),
        fetch(`/api/reports/stops?${deviceIds}${dateParam}`, { headers: { Accept: "application/json" } }),
      ]);
      if (tripsResp.ok && stopsResp.ok) {
        const trips = await tripsResp.json();
        const stops = await stopsResp.json();
        // Usando deviceMap O(1) para performance
        const stopsSeries = {
          color: chartPrimary,
          name: "Paradas",
          data: stops.map((s: any) => ({ x: deviceMap.get(s.deviceId)?.name || "", y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()] })),
        };
        const tripsSeries = {
          color: chartSecondary,
          name: "Viagens",
          data: trips.map((s: any) => ({ x: deviceMap.get(s.deviceId)?.name || "", y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()] })),
        };
        // Check if chart3 still exists (component might have unmounted during async fetch)
        if (chart3 && typeof chart3.updateOptions === 'function') {
          chart3.updateOptions({ series: [stopsSeries, tripsSeries] }, true, true);
        }
        barGroupsRef.current.bar3 = [
          ...(stops || []).map((s: any) => deviceMap.get(s.deviceId)?.name || s.deviceId),
          ...(trips || []).map((s: any) => deviceMap.get(s.deviceId)?.name || s.deviceId),
        ];
      }
    } catch (e) {
      console.error(e);
    }
  };
  fetchTripsStops();

  return () => {
    try {
      bar1.destroy();
      bar2.destroy();
      chart3.destroy();
    } catch (e) {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [devices, positions, loading, theme, deviceMap]);

  // ---- render states ----
  if (loading) {
    return (
      <LoaderContainer>
        <div>Carregando...</div>
      </LoaderContainer>
    );
  }

  if (error) {
    return (
      <LoaderContainer>
        <div>{error}</div>
      </LoaderContainer>
    );
  }

  return (
    <Root>
      <Content maxWidth={false} disableGutters>
        {/* ============ KPI Alert Bar (Compact Inline) ============ */}
        <Box sx={{ mb: 2, px: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <KpiAlertCard severity="error" elevation={0}>
              <ErrorOutlineIcon sx={{ fontSize: 18, color: '#ef4444' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className="kpi-label">{t("sharedAlerts") || "Alertas"}</span>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <span className="kpi-value">{kpiMetrics.errors}</span>
                  <Box className="kpi-trend" sx={{ color: kpiMetrics.variations.errors > 0 ? '#ef4444' : '#22c55e' }}>
                    {kpiMetrics.variations.errors > 0 ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                    {kpiMetrics.variations.errors > 0 ? '+' : ''}{kpiMetrics.variations.errors}
                  </Box>
                </Box>
              </Box>
            </KpiAlertCard>

            <KpiAlertCard severity="warning" elevation={0}>
              <WarningAmberIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className="kpi-label">{t("sharedWarnings") || "Avisos"}</span>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <span className="kpi-value">{kpiMetrics.warnings}</span>
                  <Box className="kpi-trend" sx={{ color: kpiMetrics.variations.warnings > 0 ? '#f59e0b' : '#22c55e' }}>
                    {kpiMetrics.variations.warnings > 0 ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                    {kpiMetrics.variations.warnings > 0 ? '+' : ''}{kpiMetrics.variations.warnings}
                  </Box>
                </Box>
              </Box>
            </KpiAlertCard>

            <KpiAlertCard severity="info" elevation={0}>
              <AltRouteIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className="kpi-label">{t("sharedOffline") || "Sem Sinal"}</span>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <span className="kpi-value">{kpiMetrics.offline}</span>
                  <Box className="kpi-trend" sx={{ color: kpiMetrics.variations.offline > 0 ? '#f59e0b' : '#22c55e' }}>
                    {kpiMetrics.variations.offline > 0 ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                    {kpiMetrics.variations.offline > 0 ? '+' : ''}{kpiMetrics.variations.offline}
                  </Box>
                </Box>
              </Box>
            </KpiAlertCard>

            <KpiAlertCard severity="success" elevation={0}>
              <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className="kpi-label">{t("deviceStatusOnline") || "Ativos"}</span>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <span className="kpi-value">{kpiMetrics.activePercent}%</span>
                  <Box className="kpi-trend" sx={{ color: kpiMetrics.variations.active > 0 ? '#22c55e' : '#ef4444' }}>
                    {kpiMetrics.variations.active > 0 ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                    {kpiMetrics.variations.active > 0 ? '+' : ''}{kpiMetrics.variations.active}%
                  </Box>
                </Box>
              </Box>
            </KpiAlertCard>
          </Box>
        </Box>

        {/* ============ Fleet Activity + Vehicle Condition + Trips Performance ============ */}
        <Grid container spacing={2} sx={{ mb: 2, px: 1, alignItems: 'stretch' }}>
          {/* Fleet Activity Donut */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
            <FleetActivityCard elevation={0} sx={{ flex: 1, p: 2, justifyContent: 'space-between' }}>
              <ChartTitle sx={{ mb: 0, textAlign: 'center' }}>
                {t("deviceStatus") || "Estado"}
              </ChartTitle>
              <Box sx={{ position: 'relative', width: 180, height: 180, my: 0.5 }}>
                <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                  {/* Background circle */}
                  <circle cx="50" cy="50" r="42" fill="none" stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} strokeWidth="10" />
                  {/* Progress circle */}
                  <circle
                    cx="50" cy="50" r="42"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${kpiMetrics.activePercent * 2.64} 264`}
                    style={{ transition: 'stroke-dasharray 0.8s ease' }}
                  />
                </svg>
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}>
                  <Typography sx={{ fontSize: 42, fontWeight: 800, color: '#22c55e', lineHeight: 1 }}>
                    {kpiMetrics.activePercent}%
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: textColor, opacity: 0.7, mt: 0.5 }}>
                    {t("deviceActive") || "Ativos"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Chip
                  size="small"
                  icon={<DirectionsCarIcon sx={{ fontSize: 14 }} />}
                  label={`${kpiMetrics.moving} ${t("positionMotion") || "Movimento"}`}
                  sx={{ bgcolor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', fontSize: 11 }}
                />
                <Chip
                  size="small"
                  icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                  label={`${kpiMetrics.idle} ${t("reportIdle") || "Ocioso"}`}
                  sx={{ bgcolor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', fontSize: 11 }}
                />
              </Box>
            </FleetActivityCard>
          </Grid>

          {/* Vehicle Condition */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
            <ChartContainer elevation={0} sx={{ flex: 1, justifyContent: 'center', p: 2.5 }}>
              <ChartTitle sx={{ mb: 1.5, textAlign: 'center' }}>
                {t("sharedCondition") || "Condição dos Veículos"}
              </ChartTitle>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                <ConditionCard condition="good">
                  <span className="condition-dot" />
                  <Box sx={{ flex: 1 }}>
                    <Typography className="condition-label">{t("sharedGood") || "Bom"}</Typography>
                    <Typography className="condition-value">{vehicleCondition.good}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                    {devices.length > 0 ? Math.round((vehicleCondition.good / devices.length) * 100) : 0}%
                  </Typography>
                </ConditionCard>
                <ConditionCard condition="satisfactory">
                  <span className="condition-dot" />
                  <Box sx={{ flex: 1 }}>
                    <Typography className="condition-label">{t("sharedSatisfactory") || "Regular"}</Typography>
                    <Typography className="condition-value">{vehicleCondition.satisfactory}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                    {devices.length > 0 ? Math.round((vehicleCondition.satisfactory / devices.length) * 100) : 0}%
                  </Typography>
                </ConditionCard>
                <ConditionCard condition="critical">
                  <span className="condition-dot" />
                  <Box sx={{ flex: 1 }}>
                    <Typography className="condition-label">{t("sharedCritical") || "Crítico"}</Typography>
                    <Typography className="condition-value">{vehicleCondition.critical}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                    {devices.length > 0 ? Math.round((vehicleCondition.critical / devices.length) * 100) : 0}%
                  </Typography>
                </ConditionCard>
              </Box>
            </ChartContainer>
          </Grid>

          {/* Trips Performance */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
            <ChartContainer elevation={0} sx={{ flex: 1, justifyContent: 'center', p: 2.5 }}>
              <ChartTitle sx={{ mb: 1.5, textAlign: 'center' }}>
                {t("reportTrips") || "Viagens"}
              </ChartTitle>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: textColor }}>
                  {tripsPerformance.total}
                </Typography>
                <Typography variant="caption" sx={{ color: textColor, opacity: 0.6 }}>
                  {t("sharedTotal") || "Total de viagens"}
                </Typography>
              </Box>
              <TripsProgressContainer>
                <Box className="progress-bar">
                  <MuiTooltip title={`${t("sharedOnTime") || "No prazo"}: ${tripsPerformance.onTime} (${tripsPerformance.onTimePercent}%)`} arrow>
                    <Box
                      className="progress-segment"
                      sx={{ width: `${tripsPerformance.onTimePercent}%`, bgcolor: '#22c55e' }}
                    >
                      {tripsPerformance.onTimePercent >= 5 ? `${tripsPerformance.onTimePercent}%` : ''}
                    </Box>
                  </MuiTooltip>
                  <MuiTooltip title={`${t("sharedDelayed") || "Atrasado"}: ${tripsPerformance.delayed} (${tripsPerformance.delayedPercent}%)`} arrow>
                    <Box
                      className="progress-segment"
                      sx={{ width: `${tripsPerformance.delayedPercent}%`, bgcolor: '#f59e0b' }}
                    >
                      {tripsPerformance.delayedPercent >= 5 ? `${tripsPerformance.delayedPercent}%` : ''}
                    </Box>
                  </MuiTooltip>
                  <MuiTooltip title={`${t("sharedCanceled") || "Cancelado"}: ${tripsPerformance.canceled} (${tripsPerformance.canceledPercent}%)`} arrow>
                    <Box
                      className="progress-segment"
                      sx={{ width: `${tripsPerformance.canceledPercent}%`, bgcolor: '#ef4444' }}
                    >
                      {tripsPerformance.canceledPercent >= 5 ? `${tripsPerformance.canceledPercent}%` : ''}
                    </Box>
                  </MuiTooltip>
                </Box>
                <Box className="progress-legend">
                  <MuiTooltip title={`${t("sharedOnTime") || "No prazo"}: ${tripsPerformance.onTime} viagens`} arrow>
                    <Box className="legend-item" sx={{ cursor: 'pointer' }}>
                      <Box className="legend-dot" sx={{ bgcolor: '#22c55e' }} />
                      {t("sharedOnTime") || "No prazo"} ({tripsPerformance.onTime})
                    </Box>
                  </MuiTooltip>
                  <MuiTooltip title={`${t("sharedDelayed") || "Atrasado"}: ${tripsPerformance.delayed} viagens`} arrow>
                    <Box className="legend-item" sx={{ cursor: 'pointer' }}>
                      <Box className="legend-dot" sx={{ bgcolor: '#f59e0b' }} />
                      {t("sharedDelayed") || "Atrasado"} ({tripsPerformance.delayed})
                    </Box>
                  </MuiTooltip>
                  <MuiTooltip title={`${t("sharedCanceled") || "Cancelado"}: ${tripsPerformance.canceled} viagens`} arrow>
                    <Box className="legend-item" sx={{ cursor: 'pointer' }}>
                      <Box className="legend-dot" sx={{ bgcolor: '#ef4444' }} />
                      {t("sharedCanceled") || "Cancelado"} ({tripsPerformance.canceled})
                    </Box>
                  </MuiTooltip>
                </Box>
              </TripsProgressContainer>
            </ChartContainer>
          </Grid>
        </Grid>

        {/* Block A: top pies (3) — forced flex row so items stretch evenly */}
        <Grid
          container
          spacing={2}
          sx={{
            mb: 2,
            px: 1,
            display: "flex",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer elevation={0} sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("deviceLastUpdate") || "Última comunicação"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar grupos (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = pieGroupsRef.current.pie1 || {};
                        exportGroupsAsCSV(groups, "ultima_comunicacao.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "pie1")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <PieWrapper ref={pie1Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer elevation={0} sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("positionMotion") || "Movimento"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar grupos (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = pieGroupsRef.current.pie2 || {};
                        exportGroupsAsCSV(groups, "movimento.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "pie2")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <PieWrapper ref={pie2Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer elevation={0} sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("sharedAlarms") || "Alarmes"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar grupos (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = pieGroupsRef.current.pie3 || {};
                        exportGroupsAsCSV(groups, "alarmes.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "pie3")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <PieWrapper ref={pie3Ref} />
            </ChartContainer>
          </Grid>
        </Grid>

        {/* Block B: bottom bars (3) — same approach so bars fill full width evenly */}
        <Grid
          container
          spacing={2}
          sx={{
            mb: 2,
            px: 1,
            display: "flex",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer elevation={0} sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("positionOdometer") || "Hodômetro (Kms)"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar dados (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = { Odometer: barGroupsRef.current.bar1 || [] };
                        exportGroupsAsCSV(groups, "hodometro.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "bar1")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <BarWrapper ref={bar1Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer elevation={0} sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("positionHours") || "Horímetro (Horas)"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar dados (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = { Horas: barGroupsRef.current.bar2 || [] };
                        exportGroupsAsCSV(groups, "horimetro.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "bar2")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <BarWrapper ref={bar2Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer elevation={0} sx={{ width: "100%", minHeight: ACTIVITY_BAR_WRAPPER_HEIGHT + 40 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("reportTodayActivity") || "Atividade hoje"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar dados (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = { Atividade: barGroupsRef.current.bar3 || [] };
                        exportGroupsAsCSV(groups, "atividade_hoje.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <ActivityBarWrapper ref={bar3Ref} />
            </ChartContainer>
          </Grid>
        </Grid>

        {/* Menu de exportação */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => exportChartCSV(selectedChart || "")}>
            Exportar CSV
          </MenuItem>
          <MenuItem onClick={() => exportChartXLSX(selectedChart || "")}>
            Exportar XLSX
          </MenuItem>
        </Menu>

        {/* Device Grid Dialog - Lazy Load para performance */}
        {gridOpen && (
          <Suspense fallback={<div style={{ padding: 20, textAlign: 'center' }}>Carregando...</div>}>
            <DeviceGridDialog
              open={gridOpen}
              onClose={handleCloseGrid}
              devices={devices}
              positions={positions}
              filter={gridFilter}
              colorsArr={colorsArrRef.current}
              deviceMap={deviceMap}
            />
          </Suspense>
        )}
      </Content>
    </Root>
  );
};

// ---- Wrapper simples - herda tema do AppThemeProvider ----
const OverviewPage = () => {
  // ColorMode context (mantido para compatibilidade com o botão toggle)
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        // Tema é controlado pelo app settings - este toggle apenas loga
        console.log('Theme is controlled by app settings');
      },
    }),
    []
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <Overview />
    </ColorModeContext.Provider>
  );
};

export default OverviewPage;